import type { DatasetFilterSurface } from "./datasetFilterBookmarks";
import type { DatasetMatchMode } from "./labelFilterQuery";

// 필터 조건 기억(opt-in)이다. 사용자가 시트에서 스위치를 켠 경우에만 조건 값을 저장하고 복원한다.
// 저장 대상은 facet 선택, 라벨 조건 토큰, 조합 방식이다. 검색어는 한 번만 쓰는 값이라 저장하지 않는다.
export type StoredFilterValues = {
  selection: Readonly<Record<string, readonly string[]>>;
  labelTokens: readonly { value: string; label: string }[];
  matchMode: DatasetMatchMode;
};

export type FilterMemory = {
  remember: boolean;
  values: StoredFilterValues | null;
};

const DISABLED_MEMORY: FilterMemory = { remember: false, values: null };

function storageKey(uid: string | null): string {
  return `hatchery-registry:dataset-filter-memory:${uid ?? "anonymous"}`;
}

type StoredMemoryFile = Partial<Record<DatasetFilterSurface, unknown>>;

function isStringArray(value: unknown): value is readonly string[] {
  return Array.isArray(value) && value.every((item) => typeof item === "string");
}

function parseValues(raw: unknown): StoredFilterValues | null {
  if (typeof raw !== "object" || raw === null) {
    return null;
  }
  const candidate = raw as Partial<StoredFilterValues>;
  if (candidate.matchMode !== "any" && candidate.matchMode !== "all") {
    return null;
  }
  const selectionRaw = candidate.selection;
  if (typeof selectionRaw !== "object" || selectionRaw === null) {
    return null;
  }
  const selection: Record<string, readonly string[]> = {};
  for (const [key, values] of Object.entries(selectionRaw)) {
    if (!isStringArray(values)) {
      return null;
    }
    selection[key] = values;
  }
  const tokensRaw = candidate.labelTokens;
  if (!Array.isArray(tokensRaw)) {
    return null;
  }
  const labelTokens: { value: string; label: string }[] = [];
  for (const token of tokensRaw) {
    if (
      typeof token !== "object" ||
      token === null ||
      typeof (token as { value?: unknown }).value !== "string" ||
      typeof (token as { label?: unknown }).label !== "string"
    ) {
      return null;
    }
    labelTokens.push({
      value: (token as { value: string }).value,
      label: (token as { label: string }).label,
    });
  }
  return { selection, labelTokens, matchMode: candidate.matchMode };
}

export function loadFilterMemory(uid: string | null, surface: DatasetFilterSurface): FilterMemory {
  if (typeof window === "undefined") {
    return DISABLED_MEMORY;
  }
  try {
    const raw = window.localStorage.getItem(storageKey(uid));
    if (raw === null) {
      return DISABLED_MEMORY;
    }
    const file = JSON.parse(raw) as StoredMemoryFile;
    const entry = file[surface];
    if (typeof entry !== "object" || entry === null) {
      return DISABLED_MEMORY;
    }
    const remember = (entry as { remember?: unknown }).remember === true;
    if (!remember) {
      return DISABLED_MEMORY;
    }
    return { remember: true, values: parseValues((entry as { values?: unknown }).values) };
  } catch {
    return DISABLED_MEMORY;
  }
}

// remember=false 로 저장하면 값도 함께 지운다. 그래서 끈 뒤에 숨은 상태가 남지 않는다.
export function saveFilterMemory(
  uid: string | null,
  surface: DatasetFilterSurface,
  memory: FilterMemory,
): void {
  if (typeof window === "undefined") {
    return;
  }
  try {
    const raw = window.localStorage.getItem(storageKey(uid));
    const file: StoredMemoryFile = raw !== null ? (JSON.parse(raw) as StoredMemoryFile) : {};
    const next: StoredMemoryFile = {
      ...file,
      [surface]: memory.remember
        ? { remember: true, values: memory.values }
        : { remember: false, values: null },
    };
    window.localStorage.setItem(storageKey(uid), JSON.stringify(next));
  } catch {
    // 비공개 또는 제한된 브라우저 환경에서는 Storage 를 쓸 수 없을 수 있다.
  }
}
